import * as path from "node:path";
import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { appHealthFailureCopy, checkAppHealth, readAppSecrets } from "@m8t-stack/github-app-auth";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { getAzAccount, getCallerObjectId } from "../../lib/auth.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { checkPreconditions } from "../../lib/preconditions.js";
import { getAgentVersion } from "../../lib/foundry-agent-get.js";
import { deployHostedWorker } from "../../lib/coder-deploy.js";
import { resolvePersona } from "../../lib/persona.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { discoverKvUri, resolvePlatformRepoRoot } from "../../lib/brain-paths.js";
import { resolveAppInstallation } from "../../lib/app-install.js";
import { ensureDeliveryGrant, normalizeKvUri } from "../../lib/delivery-grant.js";
import { grantContributor, grantUserAccessAdmin, callerCanAssignRoles } from "../../lib/rbac.js";
import { enableA2a } from "../../lib/a2a-enable.js";
import { resolveBridgeUrl } from "../../lib/bridge-url.js";
import { listModelQuota, modelQuotaVerdict } from "../../lib/quota.js";
import { assertPlatformLive } from "../../lib/platform-live.js";
import { stagePublicImageIfNeeded } from "../../lib/image-staging.js";
import { makeWarningSink } from "../../lib/warnings.js";
import { provisionAcsEmail } from "../../lib/acs-provision.js";
import { CommunicationServiceManagementClient } from "@azure/arm-communication";

const SIZE_PRESETS: Partial<Record<string, { cpu: string; memory: string }>> = {
  small: { cpu: "0.5", memory: "1Gi" },
  medium: { cpu: "1", memory: "2Gi" },
  large: { cpu: "2", memory: "4Gi" },
};
const DEFAULT_REGISTRY = "ghcr.io/m8t-labs";
const DEFAULT_IMAGE = "m8t-azure-executor";
const DEFAULT_TAG = "v0.1.2"; // public GHCR pin (agents/azure-executor/pyproject.toml#version); bump when a new agent version ships
const DEFAULT_MODEL = "gpt-5-mini";
const NAME_RE = /^[a-z0-9-]+$/;

export class AzureExecDeployCommand extends M8tCommand {
  static paths = [["azure-exec", "deploy"]];
  static usage = Command.Usage({
    description: "Deploy the Azure executor as a hosted Foundry worker (az CLI + tiered ops).",
    details:
      "Creates a hosted agent version from a container image (defaults to the public GHCR image ghcr.io/m8t-labs/m8t-azure-executor — no local build needed), grants its identity Foundry User + Contributor (at --scope) + Key Vault Secrets User (brain KV), polls to active, and a2a-enables it as a target. Override with --image/--image-tag for a bring-your-own-registry image (e.g. a private ACR ref, which must be pushed first). Contributor scope is REQUIRED — pass --scope or --resource-group. Pass --grant-access-admin to additionally grant User Access Administrator (enables human-approved Tier-2 role/delete ops). Post-2026-06-25 Foundry projects require an authenticated pull, so the public image (default ghcr.io/m8t-labs/m8t-azure-executor) is staged into the customer's Azure Container Registry first (created if missing, then `az acr import` — server-side, no local build) and deployed from that ACR ref; re-running with a newer --image-tag updates the image.",
    examples: [
      [
        "Deploy scoped to a resource group",
        "$0 azure-exec deploy azexec --resource-group rg-test --brain m8t-labs/azure-exec-smoke-brain --gateway-url https://<gw>/api/a2a/mcp",
      ],
    ],
  });

  name = Option.String();
  image = Option.String("--image");
  imageTag = Option.String("--image-tag");
  size = Option.String("--size");
  scope = Option.String("--scope");
  resourceGroup = Option.String("--resource-group");
  modelDeployment = Option.String("--model-deployment");
  env = Option.Array("--env");
  brain = Option.String("--brain");
  kvUri = Option.String("--kv-uri");
  gatewayUrl = Option.String("--gateway-url");
  endpoint = Option.String("--endpoint");
  subscription = Option.String("--subscription");
  output = Option.String("--output");
  skipQuotaCheck = Option.Boolean("--skip-quota-check", false);
  grantAccessAdmin = Option.Boolean("--grant-access-admin", false);
  enableEmail = Option.Boolean("--enable-email", false);

  async executeCommand(): Promise<number> {
    if (!NAME_RE.test(this.name)) {
      throw new LocalCliError({
        code: "USAGE",
        message: `Invalid worker name '${this.name}'. Use lowercase letters, digits, and hyphens only.`,
      });
    }
    if (typeof this.brain !== "string" || !this.brain.includes("/")) {
      throw new LocalCliError({
        code: "USAGE",
        message: "--brain owner/repo is required (the executor delivers proof to a brain).",
        hint: "Example: --brain m8t-labs/azure-exec-smoke-brain",
      });
    }

    const size = (this.size ?? "large").toLowerCase();
    const preset = SIZE_PRESETS[size];
    if (!preset) {
      throw new LocalCliError({
        code: "USAGE",
        message: `Unknown --size '${this.size ?? ""}'. Use small, medium, or large.`,
      });
    }

    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;
    const onProgress =
      mode === "pretty" ? (m: string) => this.context.stderr.write(`  ${colors.dim(m)}\n`) : undefined;

    // Advisories NEVER go to stderr: the installer's retry classifier parses this
    // command's stderr as JSON, and one non-JSON line there makes it read a
    // transient failure as permanent. Pretty mode still shows them - on stdout.
    const warnings = makeWarningSink((m) => {
      if (mode !== "json") this.context.stdout.write(`${colors.hint("note:")} ${m}\n`);
    });

    const credential = new DefaultAzureCredential();
    const account = await getAzAccount();
    const subscriptionId = this.subscription ?? account.subscriptionId;
    const callerObjectId = await getCallerObjectId();

    // Footgun guard: refuse if the platform isn't deployed yet (clear message
    // instead of a cryptic unreachable-Foundry failure downstream).
    await assertPlatformLive({ subscriptionId, interactive });

    const grantScope = this.resolveScope(subscriptionId);

    // Fail fast on the high-privilege grant BEFORE any side effects (deploy, Contributor):
    // an executor that never gets UAA is just Contributor-only (safe), but the operator
    // should learn they lack the identity-plane role before we burn a deploy.
    // `=== true`: clipanion holds a truthy Option descriptor until argv parse, so a
    // bare check would enable this high-privilege grant by default under test construction.
    if (this.grantAccessAdmin === true) {
      const canAssign = await callerCanAssignRoles({ credential, scope: grantScope, callerObjectId });
      if (!canAssign) {
        throw new LocalCliError({
          code: "CANNOT_GRANT_UAA",
          message: `You lack Owner / User Access Administrator at ${grantScope}, so you cannot grant User Access Administrator to the executor.`,
          hint: "Re-run from a principal with Owner or UAA at that scope, or drop --grant-access-admin.",
        });
      }
    }

    // `=== true`: guard against the truthy Option descriptor that clipanion holds until
    // argv parse — same idiom as grantAccessAdmin above.
    if (this.enableEmail === true && (typeof this.resourceGroup !== "string" || this.resourceGroup.length === 0)) {
      throw new LocalCliError({
        code: "USAGE",
        message: "--enable-email requires --resource-group (the ACS resource lands in the Contributor-granted RG so the executor's identity can send).",
        hint: "Pass --resource-group <rg> (the same scope the executor is granted Contributor on).",
      });
    }

    const imageInput = this.image ?? DEFAULT_IMAGE;
    const repoRef = imageInput.includes("/") ? imageInput : `${DEFAULT_REGISTRY}/${imageInput}`;
    const requestedImage = `${repoRef}:${this.imageTag ?? DEFAULT_TAG}`;

    const project = await resolveFoundryProject({
      credential,
      subscriptionId,
      interactive,
      endpoint: this.endpoint,
    });

    let currentMetadata: Record<string, string> = {};
    try {
      const current = await getAgentVersion({ credential, projectEndpoint: project.endpoint, agentName: this.name });
      currentMetadata = { ...(current.metadata ?? {}) };
    } catch {
      /* first deploy */
    }

    const staged = await stagePublicImageIfNeeded({ image: requestedImage, project });
    for (const n of staged.notes) {
      warnings.add(n);
    }
    const image = staged.image;

    const processEnv = this.context.env;
    const kvUri = discoverKvUri(processEnv, typeof this.kvUri === "string" ? normalizeKvUri(this.kvUri) : undefined);

    onProgress?.("checking GitHub App health…");
    const health = await checkAppHealth({ credential, kvUri });
    if (!health.ok) {
      throw new LocalCliError({
        code: "APP_HEALTH_FAILED",
        ...appHealthFailureCopy(health),
      });
    }
    const { slug, appId, privateKeyPem } = await readAppSecrets({ credential, kvUri });
    const [owner, repoName] = this.brain.split("/");
    const installationId = await resolveAppInstallation({
      owner,
      repo: repoName,
      slug,
      appId,
      privateKeyPem,
      write: (s) => this.context.stdout.write(s),
    });

    let acsEnv: Record<string, string> = {};
    // The fail-fast above guarantees resourceGroup is a non-empty string when enableEmail
    // is set; the `typeof` here narrows it for TS (a const capture preserves the narrowing
    // across the provisioning call) without a forbidden non-null assertion.
    if (this.enableEmail === true && typeof this.resourceGroup === "string") {
      const resourceGroup = this.resourceGroup;
      onProgress?.("provisioning ACS Email (managed domain)…");
      const acsClient = new CommunicationServiceManagementClient(credential, subscriptionId);
      const acs = await provisionAcsEmail({
        client: acsClient,
        subscriptionId,
        resourceGroup,
        name: this.name,
        dataLocation: "United States",
        onProgress,
      });
      acsEnv = { M8T_ACS_ENDPOINT: acs.endpoint, M8T_ACS_SENDER: acs.sender };
    }

    const env: Record<string, string> = {
      MODEL_DEPLOYMENT_NAME: this.modelDeployment ?? DEFAULT_MODEL,
      REASONING_EFFORT: "low",
      GITHUB_APP_INSTALLATION_ID: installationId,
      AZURE_KEYVAULT_URI: kvUri,
      ...acsEnv,
    };
    for (const pair of this.env ?? []) {
      const eq = pair.indexOf("=");
      if (eq <= 0) {
        throw new LocalCliError({
          code: "USAGE",
          message: `Invalid --env '${pair}'. Expected KEY=VALUE.`,
        });
      }
      env[pair.slice(0, eq)] = pair.slice(eq + 1);
    }

    const { warnings: preconditionWarnings } = await checkPreconditions({
      credential,
      subscriptionId,
      project,
      image,
      callerObjectId,
    });
    for (const w of preconditionWarnings) {
      warnings.add(w);
    }

    // `!== true`: run the quota check unless --skip-quota-check was explicitly passed
    // (clipanion's Option descriptor is truthy until argv parse, so `!this.skipQuotaCheck`
    // would skip the check by default under test construction).
    if (this.skipQuotaCheck !== true) {
      const usages = await listModelQuota(project.region);
      const { verdict, quotad } = modelQuotaVerdict(usages, env.MODEL_DEPLOYMENT_NAME);
      if (verdict === "no_quota") {
        throw new LocalCliError({
          code: "MODEL_NO_QUOTA",
          message: `Model '${env.MODEL_DEPLOYMENT_NAME}' has 0 quota in ${project.region}.`,
          hint: `Pick a quota'd model (e.g. ${quotad[0] ?? "gpt-5-mini"}) or --skip-quota-check.`,
        });
      }
    }

    const { persona: personaName, personaVersion } = resolvePersona("azure-executor");

    const result = await deployHostedWorker({
      credential,
      subscriptionId,
      project,
      name: this.name,
      image,
      cpu: preset.cpu,
      memory: preset.memory,
      env,
      metadata: { ...currentMetadata, source: "m8t", kind: "hosted", persona: personaName, personaVersion, productVisibility: "internal" },
      onProgress,
    });

    onProgress?.(`granting Contributor at ${grantScope}…`);
    await grantContributor({
      credential,
      subscriptionId,
      scope: grantScope,
      principalId: result.principalId,
    });

    // `=== true`: only grant UAA when the flag was explicitly passed (the clipanion
    // Option descriptor is truthy until argv parse).
    if (this.grantAccessAdmin === true) {
      // Caller's assign-roles right was verified up front (before the deploy); grant UAA now
      // that the agent identity exists.
      onProgress?.(`granting User Access Administrator at ${grantScope}…`);
      await grantUserAccessAdmin({
        credential,
        subscriptionId,
        scope: grantScope,
        principalId: result.principalId,
      });
    }

    onProgress?.("granting Key Vault Secrets User for delivery…");
    await ensureDeliveryGrant({
      credential,
      subscriptionId,
      principalId: result.principalId,
      kvUri,
    });

    onProgress?.("a2a-enabling as a target…");
    const personaPath = path.join(resolvePlatformRepoRoot("'m8t azure-exec deploy'"), "personas", "azure-executor", "persona.md");
    const bridgeUrl = resolveBridgeUrl({
      flag: typeof this.gatewayUrl === "string" ? this.gatewayUrl : undefined,
      env: processEnv,
    });
    await enableA2a({
      credential,
      projectEndpoint: project.endpoint,
      projectArmId: `${project.accountScope}/projects/${project.projectName}`,
      agentName: this.name,
      personaPath,
      bridgeUrl,
      onProgress,
    });

    if (mode === "json") {
      this.context.stdout.write(
        renderJson({
          name: this.name,
          version: result.version,
          status: result.status,
          persona: personaName,
          image,
          scope: grantScope,
          endpoint: project.endpoint,
          agentPrincipalId: result.principalId,
          warnings: warnings.list(),
        }) + "\n",
      );
      return 0;
    }

    this.context.stdout.write(
      `${colors.success("✓")} deployed Azure executor ${colors.field(this.name)} (version ${result.version}, ${result.status}, ${size}).\n`,
    );
    this.context.stdout.write(`  scope: ${grantScope}\n  image: ${image}\n`);
    this.context.stdout.write(
      this.grantAccessAdmin === true
        ? `  ${colors.hint("next:")} invoke it directly or via a2a; it provisions (Tier 0/1) and executes human-approved Tier-2 (role/delete).\n`
        : `  ${colors.hint("next:")} invoke it directly or via a2a; it provisions (Tier 0/1) and refuses Tier-2.\n`,
    );
    return 0;
  }

  private resolveScope(subscriptionId: string): string {
    if (typeof this.scope === "string" && this.scope.startsWith("/subscriptions/")) return this.scope;
    if (typeof this.resourceGroup === "string" && this.resourceGroup.length > 0) {
      return `/subscriptions/${subscriptionId}/resourceGroups/${this.resourceGroup}`;
    }
    throw new LocalCliError({
      code: "USAGE",
      message:
        "A Contributor scope is required. Pass --resource-group <rg> or --scope <full-arm-id>.",
      hint: "This worker is least-privilege: there is no default scope. Scope it to a resource group (or pass a full --scope id).",
    });
  }
}
